import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { canViewCoverage, getTerritoryCoverage, positiveBusinessId } from '../services/TerritoryCoverage';

export const COVERAGE_STALE_MS = 120000;
let selectionSequence = 0;
export const coverageIsStale = (data, error, now) => !!data && (
  !!error || now - Date.parse(data.as_of_utc) >= COVERAGE_STALE_MS ||
  Date.parse(data.as_of_utc) - now > 30000
);

export function useTerritoryCoverage(businessId, authorized = false) {
  const user = useSelector((state) => state.user.user);
  const sessionVersion = useSelector((state) => state.user.sessionVersion || 0);
  const enabled = canViewCoverage(user) && authorized && positiveBusinessId(businessId);
  const scope = JSON.stringify([sessionVersion, user?.id, user?.roles?.DeliveryOperationsPortal, enabled, Number(businessId)]);
  const selection = useRef(null);
  if (selection.current?.scope !== scope) selection.current = { scope, revision: ++selectionSequence };
  const [now, setNow] = useState(Date.now());
  const [foreground, setForeground] = useState(document.visibilityState !== 'hidden');
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 10000);
    const visible = () => { setForeground(document.visibilityState !== 'hidden'); setNow(Date.now()); };
    document.addEventListener('visibilitychange', visible);
    return () => { clearInterval(tick); document.removeEventListener('visibilitychange', visible); };
  }, []);
  const query = useQuery(['territory-coverage-v2', sessionVersion, user?.id,
    user?.roles?.DeliveryOperationsPortal, enabled, Number(businessId), selection.current.revision], () => {
    if (!enabled) throw new Error('Select an authorized business first.');
    return getTerritoryCoverage(businessId);
  }, {
    enabled, staleTime: 0, keepPreviousData: false, retry: false,
    refetchInterval: enabled && foreground ? 60000 : false,
    refetchIntervalInBackground: false, refetchOnWindowFocus: true,
  });
  const data = enabled && query.error?.status !== 403 && query.error?.response?.status !== 403
    ? query.data : undefined;
  return { ...query, data, error: enabled ? query.error : undefined, now,
    stale: coverageIsStale(data, query.error, now),
    refetch: () => enabled ? query.refetch() : Promise.resolve() };
}
